import {
  Component,
  inject,
  output,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';

import { MessageService, ObserveIntersectingDirective, ProgressComponent } from '@factor_ec/ui';
import { Operation } from '@factor_ec/utils';

import { AppService } from '@/core/app.service';
import { Group } from '@/main/models/group';
import { SessionService } from '@/core/session.service';
import { GroupRepository } from '@/main/repositories/group-repository';

/**
 * @description Componente para crear o editar un grupo
 * @class GroupComponent
 */
@Component({
  selector: 'app-group',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormField,
    MatInputModule,
    ProgressComponent,
    ObserveIntersectingDirective
  ],
  templateUrl: './group.component.html',
  styleUrl: './group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupComponent {
  // Dependency injection
  public readonly appService = inject(AppService);
  private readonly dialogRef = inject(MatDialogRef<GroupComponent>);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogData = inject<{ group: Group } | undefined>(
    MAT_DIALOG_DATA,
    { optional: true }
  );
  private readonly groupRepository = inject(GroupRepository);
  private readonly messageService = inject(MessageService);
  private readonly sessionService = inject(SessionService);

  // Events
  afterSubmit = output<Operation>();

  // Properties
  form: FormGroup = new FormGroup({});
  mutations = this.groupRepository.mutations();

  constructor() {
    this.initForm();
  }

  /**
   * @description Inicializa el formulario reactivo con valores por defecto o datos del diálogo
   * @private
   */
  private initForm(): void {
    const description = this.dialogData?.group?.description ?? '';
    this.form = this.formBuilder.group({
      sequence: [null],
      description: [
        description,
        [Validators.required, Validators.maxLength(255)]
      ]
    });
    if (this.dialogData?.group) {
      this.form.patchValue({
        sequence: this.dialogData.group.sequence,
        description: this.dialogData.group.description
      });
    }
  }

  /**
   * @description Envía el formulario y cierra el diálogo emitiendo el resultado
   * @returns {Promise<void>}
   */
  async submit(): Promise<void> {
    if (this.form.valid) {
      const lenderId = this.sessionService.settings()?.lenderId;
      const data = {
        lender: {
          documentType: 'N',
          id: lenderId
        },
        group: this.form.value
      };
      try {
        this.form.disable();
        const method = this.dialogData?.group?.sequence ? 'update' : 'add';
        let response;
        if (method === 'update') {
          response = await this.mutations.update(data);
          this.messageService.show(
            $localize`A new group has been updated successfully.`,
            { class: 'ft-message--success', icon: 'check--circle' }
          );
        } else {
          response = await this.mutations.create(data);
          this.messageService.show(
            $localize`A new group has been created successfully.`,
            { class: 'ft-message--success', icon: 'check--circle' }
          );
        }
        data.group.sequence = response?.data;
        this.afterSubmit.emit({ method, entity: data.group });
        this.dialogRef.close(data);
      } finally {
        this.form.enable();
      }
    }
  }
}
